import { useEffect, useRef, useState } from 'react'
import { Formik } from 'formik'
import { isEqual } from 'lodash'

import { Button } from 'components/Button'
import Styles from './ArticleStyle.module.scss'
import SettingsStyles from 'components/Settings/SettingsModal.module.scss'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import client from 'api/client'
import { ArticleStyle as ArticleStyleType, Workspace } from 'types'
import { PreviewNodes } from './constants'
import { MetaTags } from 'components/MetaTags'
import { ProsePreview } from 'common/ProsePreview/ProsePreview'
import { WORKSPACE_ARTICLE_STYLE_PATH } from 'paths'
import { Spinner } from 'common/Spinner/Spinner'
import { StyleSection } from './StyleSection/StyleSection'
import { handleErrorResponseWithMessage } from 'api/utils/handleErrorResponseWithMessage'
import { schema } from 'prose-core'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useComputedEditorStyles } from './useComputedEditorStyles'

const CONFIRM_RESET_MESSAGE = 'Do you really want to reset all styles to the default?'
interface Props {
  workspace: Workspace
}

const UPDATE_SUCCESS_MESSAGE = 'Document styles saved successfully. Refresh your docs to see updated changes.'

export const ArticleStyle = ({ workspace }: Props) => {
  const [articleStyle, setArticleStyle] = useState<ArticleStyleType>()
  const previewRef = useRef<HTMLDivElement>(null)
  const [, setPreviewInitialized] = useState(false)
  const defaultStyles = useComputedEditorStyles(previewRef.current)

  const workspace_key = workspace?.key
  useEffect(() => {
    const loadWorkspaceSettings = async () => {
      if (!workspace_key) return
      const response = await client.getWorkspaceSettings(workspace_key).then(handleErrorResponseWithMessage())

      if (response?.type === SUCCESS_RESPONSE_TYPE) {
        setArticleStyle(response.data.company.article_style)
      }
    }

    loadWorkspaceSettings()
  }, [workspace_key])

  const updateArticleStyle = async (values: ArticleStyleType) => {
    if (!workspace) return

    const response = await client
      .updateWorkspaceSettings({ workspace: { article_style_attributes: values } }, workspace.key)
      .then(handleErrorResponseWithMessage())

    if (response?.type === SUCCESS_RESPONSE_TYPE) {
      setArticleStyle(response.data.company.article_style)
      showToastMessage(UPDATE_SUCCESS_MESSAGE)
    }
  }

  const resetStyles = (setValues: any) => async () => {
    if (!workspace) return
    if (!window.confirm(CONFIRM_RESET_MESSAGE)) return

    const response = await client
      .updateWorkspaceSettings({ workspace: { article_style_attributes: { _destroy: true } } }, workspace.key)
      .then(handleErrorResponseWithMessage())

    if (response?.type === SUCCESS_RESPONSE_TYPE) {
      setArticleStyle(response.data.company.article_style)
      showToastMessage(UPDATE_SUCCESS_MESSAGE)
      setValues(response.data.company.article_style)
    }
  }

  if (!articleStyle) return <Spinner />

  return (
    <>
      <div className={Styles.defaultsPreview} ref={previewRef}>
        <ProsePreview nodes={PreviewNodes} onInit={() => setPreviewInitialized(true)} schema={schema} />
      </div>

      <MetaTags
        description="Change the default styles for all docs in your workspace."
        title="Document Styles | Blaze"
        url={WORKSPACE_ARTICLE_STYLE_PATH}
      />
      <main className={Styles.container}>
        <Formik enableReinitialize initialValues={articleStyle} onSubmit={updateArticleStyle}>
          {({ handleSubmit, setFieldValue, setValues, values, resetForm }) => (
            <form onSubmit={handleSubmit}>
              <div className={Styles.previewWrapper}>
                <div>
                  <header className={SettingsStyles.subHeader}>
                    <h1>Document Styles</h1>
                    <p>Changing the text styles on this page will change all docs in your workspace.</p>
                  </header>

                  <div className={Styles.previewContainer}>
                    <div className={Styles.previewHint}>Preview</div>

                    <ProsePreview articleStyles={[values]} nodes={PreviewNodes} schema={schema} />
                  </div>
                </div>

                <div className={Styles.articleStyle}>
                  <header>
                    <Button type="submit" isDisabled={isEqual(values, articleStyle)}>
                      Save
                    </Button>
                    <Button
                      type="button"
                      className={Styles.cancelButton}
                      onPress={() => resetForm()}
                      variant="tertiary"
                    >
                      Cancel
                    </Button>
                  </header>

                  <StyleSection
                    defaults={defaultStyles.p}
                    name="body"
                    property={values.body}
                    setFieldValue={setFieldValue}
                    title="Normal Text"
                  />

                  <StyleSection
                    defaults={defaultStyles.h1}
                    name="h1"
                    property={values.h1}
                    setFieldValue={setFieldValue}
                    title="Title"
                  />

                  <StyleSection
                    defaults={defaultStyles.h2}
                    name="h2"
                    property={values.h2}
                    setFieldValue={setFieldValue}
                    title="Header 1"
                  />

                  <StyleSection
                    defaults={defaultStyles.h3}
                    name="h3"
                    property={values.h3}
                    setFieldValue={setFieldValue}
                    title="Header 2"
                  />

                  <StyleSection
                    defaults={defaultStyles.h4}
                    name="h4"
                    property={values.h4}
                    setFieldValue={setFieldValue}
                    title="Header 3"
                  />

                  <button className={Styles.resetButton} onClick={resetStyles(setValues)} type="button">
                    Reset Styles to Default
                  </button>
                </div>
              </div>
            </form>
          )}
        </Formik>
      </main>
    </>
  )
}

export default ArticleStyle
